/**
 * useCampaigns
 *
 * Feature hook for the standalone Campaigns page. Loads active campaigns on
 * page init and exposes the "open ranking" gate.
 *
 * Mirrors Flutter's standalone CampaignsScreen path:
 * - lib/presentation/screens/campaigns/bloc/campaigns_bloc.dart:37-41 — initial
 *   `isLoading = true`; on `CampaignsRequested` loads `getActiveCampaigns()` and
 *   emits `isLoading = false` with the campaigns. The standalone screen autoloads
 *   on create (`!fromHomeScreen` → `autoLoad = true`).
 * - lib/presentation/screens/campaigns/campaigns_screen.dart:25-46 — the
 *   `startDate > now` gate that decides whether the ranking sheet may open.
 *
 * Architecture: Component → Hook → UseCase → Service → Data layer.
 *
 * The hook stays free of `useBasePage` and free of UI (sheets): the page owns the
 * lifecycle wiring and passes UI callbacks in, mirroring `usePremiumPackages`.
 */
import { useState, useCallback } from 'react';
import { GetActiveCampaignsUseCase } from '@/domain/usecases/campaign/getActiveCampaignsUseCase';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';

export const useCampaigns = () => {
  const [campaigns, setCampaigns] = useState<CampaignEntity[]>([]);
  // Matches Flutter's initial `isLoading = true` (campaigns_state.dart:10).
  const [isLoading, setIsLoading] = useState(true);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    const useCase = new GetActiveCampaignsUseCase();
    const result = await useCase.execute();
    setCampaigns(result);
    setIsLoading(false);
  }, []);

  // Memoized lifecycle callback the page passes into `useBasePage({ onInit })`.
  const onPageInit = useCallback(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Negation of Flutter's `startDate > now` gate
  // (campaigns_screen.dart:29). When false, the page shows the "not started"
  // info sheet and returns instead of opening the ranking sheet.
  const canOpenRanking = useCallback(
    (campaign: CampaignEntity): boolean => campaign.startDate <= Date.now(),
    [],
  );

  return {
    campaigns,
    isLoading,
    onPageInit,
    canOpenRanking,
  };
};
